import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Text,
  HStack,
  FormControl,
  FormLabel,
  Select,
  VStack,
} from '@chakra-ui/react'
import { Clock3, FileEdit } from 'lucide-react'
import type { ParentTicketStatus } from '@/types/parentTicket'
import { useThemeColors } from '@/hooks/useThemeColors'

type Option = { label: string; value: string }

type Props = {
  statusLabel: string
  slaDueAt: string | null
  statusValue: ParentTicketStatus
  priorityValue?: string
  statusOptions: Option[]
  priorityOptions?: Option[]
  onChangeStatus: (value: string) => void
  onChangePriority?: (value: string) => void
  isReadOnly?: boolean
  hidePriority?: boolean
  onAddComment?: () => void
}


function formatLiveDateTime(ts: number): string {
  return new Date(ts).toLocaleString('en-PH', {
    timeZone: 'Asia/Manila',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })
}

export default function StatusSlaCard({
  statusLabel,
  slaDueAt,
  statusValue,
  priorityValue,
  statusOptions,
  priorityOptions,
  onChangeStatus,
  onChangePriority,
  isReadOnly = false,
  hidePriority = false,
  onAddComment,
}: Props) {
  const colors = useThemeColors()

  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  return (
    <Box
      bg="white"
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      overflow="hidden"
    >
      <Box px={4} py={3} borderBottom="1px solid" borderColor="gray.100">
        <Text fontWeight="700" color={colors.primary}>
          Ticket Actions
        </Text>
      </Box>

      <VStack spacing={4} align="stretch" p={4}>
        {/* Live date & time */}
        <HStack spacing={2} align="center">
          <Box
            w="28px"
            h="28px"
            display="grid"
            placeItems="center"
            borderRadius="md"
            border="1px solid"
            borderColor="blue.200"
            bg="blue.50"
          >
            <Clock3 size={16} color="#3182ce" />
          </Box>
          <Text fontSize="sm" fontWeight="600" color="gray.700">
            {formatLiveDateTime(now)}
          </Text>
        </HStack>

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
            Status:
          </FormLabel>
          <Select
            value={statusValue}
            onChange={(e) => onChangeStatus(e.target.value)}
            size="md"
            h="44px"
            borderRadius="md"
            borderColor="gray.200"
            bg="white"
            isDisabled={isReadOnly}
          >
            {statusOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </FormControl>

        {!hidePriority && priorityOptions && onChangePriority && (
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
              Priority:
            </FormLabel>
            <Select
              value={priorityValue}
              onChange={(e) => onChangePriority(e.target.value)}
              size="md"
              h="44px"
              borderRadius="md"
              borderColor="gray.200"
              bg="white"
              isDisabled={isReadOnly}
            >
              {priorityOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </FormControl>
        )}

        {onAddComment && (
          <Button
            leftIcon={<FileEdit size={16} />}
            variant="outline"
            colorScheme="blue"
            size="sm"
            w="full"
            onClick={onAddComment}
          >
            Add Update
          </Button>
        )}
      </VStack>
    </Box>
  )
}
